import { useQuery } from '@tanstack/react-query';
import { Siren } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useServerOptions, type IncidentListRow } from '../lib/monitoring';
import { MonitorBanner } from '../components/MonitorBanner';
import { IncidentsTable } from './MonitoringUrls';
import { Card, EmptyState, ErrorState, LoadingBlock, PageHeader, Pagination, Select } from '../components/ui';

/**
 * Monitoring › Incidents (operator's spec, 2026-10-20: "سجل الأعطال وتوقيت
 * بدايتها ونهايتها" — the log of outages, with when each began and ended).
 * Every outage of every URL, newest first, by server and project.
 */
export function MonitoringIncidentsPage() {
  const { t, localized } = useI18n();
  const [search] = useSearchParams();
  const servers = useServerOptions();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({ open: search.get('open') ?? '' });
  const set = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['monitoring', 'incidents', page, filters],
    queryFn: () => api.get<Paginated<IncidentListRow>>('/monitoring/incidents', { page, pageSize: 50, ...filters }),
    refetchInterval: 30_000,
  });

  const projectChoices = [
    ...new Map(
      (servers.data ?? [])
        .filter((s) => !filters.serverId || s.id === filters.serverId)
        .flatMap((s) => s.projects)
        .map((p) => [p.id, p]),
    ).values(),
  ];

  return (
    <>
      <PageHeader title={t('monitoring.incidents.title')} subtitle={t('monitoring.url.incidentsHint')} count={data?.total} />
      <MonitorBanner />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <Select value={filters.open ?? ''} onChange={(e) => set('open', e.target.value)}>
            <option value="">{t('monitoring.incidents.all')}</option>
            <option value="true">{t('monitoring.incidents.open')}</option>
            <option value="false">{t('monitoring.incidents.over')}</option>
          </Select>
          <Select value={filters.serverId ?? ''} onChange={(e) => setFilters({ ...filters, serverId: e.target.value, projectId: '' })}>
            <option value="">{t('monitoring.filter.allServers')}</option>
            {(servers.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
          <Select value={filters.projectId ?? ''} onChange={(e) => set('projectId', e.target.value)}>
            <option value="">{t('monitoring.filter.allProjects')}</option>
            {projectChoices.map((p) => (
              <option key={p.id} value={p.id}>{`${p.code} · ${localized(p)}`}</option>
            ))}
          </Select>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<Siren className="h-8 w-8" />} title={t('monitoring.incidents.empty')} hint={t('monitoring.incidents.emptyHint')} />
        ) : (
          <>
            <IncidentsTable incidents={data.items} showUrl showServer />
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
